import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { useVideosByIdQuery } from 'api';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import ImageHeroWithRibbon from 'components/pages/homepage/ImageHeroWithRibbon';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import { useEffect, useState, type JSX } from 'react';
import { PrismicNextImage } from '../../PrismicNextImage';

import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';

/**
 * Props for `PartnershipsTrendingSlice`.
 */
export type PartnershipsTrendingSliceProps = {
  context: {
    mixpanelTitle: string;
  };
} & SliceComponentProps<Content.PartnershipsTrendingSliceSlice>;

/**
 * Component for "PartnershipsTrendingSlice" Slices.
 */
const PartnershipsTrendingSlice = ({
  slice,
  context
}: PartnershipsTrendingSliceProps): JSX.Element => {
  const [talks, setTalks] = useState([]);
  const hiddenClassName = cx('pt-10', {
    'hidden h-0': slice.primary.is_hidden
  });

  const isMobileWidth = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });

  const sponsorClasses = cx('gap-2', {
    '-mt-4 flex w-full items-center justify-center bg-[rgba(246,246,246,1)] py-7':
      isMobileWidth,
    'flex flex-col': !isMobileWidth
  });

  const talkIds = [
    slice.primary.main_talk_id,
    slice.primary.second_talk_id,
    slice.primary.third_talk_id
  ];

  const { data, loading } = useVideosByIdQuery({
    variables: { id: talkIds.map(id => Number(id)) },
    ssr: true
  });

  useEffect(() => {
    if (!loading && data) {
      setTalks(data.videos.edges.map(({ node }) => ({ video: node })));
    }
  }, [data, loading]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={hiddenClassName}
    >
      <ImageHeroWithRibbon
        talks={talks}
        title={slice.primary.title}
        // @ts-ignore
        textHeader={slice.primary.text_header[0].text}
        // @ts-ignore
        headerLink={slice.primary.header_link.url}
        headerLinkText={slice.primary.header_link_text}
        mixpanelTitle={context.mixpanelTitle}
      />
      <PrismicConditionalLink
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        field={slice.primary.partner_link}
      >
        <ContentContainer
          className="relative mt-0 pb-12 lg-tui:-mt-11"
          noPadding={isMobileWidth}
        >
          <div className={sponsorClasses}>
            <Text
              useNewTextStyles
              tag="p"
              variant="unset"
              weight="font-normal"
              color={{ override: '#808080' }}
              UNSAFE_className="text-[9.685px] leading-[135%]"
            >
              {slice.primary.title} sponsored by
            </Text>
            <PrismicNextImage
              field={slice.primary.partner_logo}
              style={{
                objectFit: 'contain',
                width: 120,
                height: 40
              }}
            />
          </div>
        </ContentContainer>
      </PrismicConditionalLink>
    </section>
  );
};

export default PartnershipsTrendingSlice;
